/**
 * 顧客契約 CSV アップロード画面 Step1「ファイル選択」パネル内の記入ガイド表・雛形データ
 * (common.md AC-DUC-037 / AC-DUC-031)。
 *
 * - 記入ガイド表: 列名 / 必須 / 説明 / 入力形式 / 記入例。文言の正本は spec `customer-contract.md` §3
 *   「画面表示（記入ガイド表）」の表で、括弧のみ design 規約 (半角 + 前に半角スペース 1 つ) に合わせる。
 *   spec に無い「地点Id」「区分」「契約者Id」「契約者名」「契約電力の変更」の 5 行は、spec 改訂までの仮の文言
 *   (列の意味はモックの契約推移タブの CSV に合わせる)。供給地点特定番号の行は、番号を行のキーにしない
 *   (契約は必須、見込みは任意) 判断に合わせて書き換えている。列順は `CUSTOMER_CONTRACT_CSV_COLUMNS` と完全一致。
 * - 雛形の記入例: 代表 3 パターン (新しい需要家の新規契約 / 登録済みの需要家への新規見込み /
 *   登録済みの地点の変更)。新規の 2 行はエリアを「記入例」に、変更の行は地点Id をダミー値にして、
 *   そのまま取り込んでも行エラーになり誤登録されないようにする。
 */
import type { ColumnGuideEntry } from '~/models/columnGuide'
import {
  CUSTOMER_CONTRACT_CSV_COLUMNS,
  type CustomerContractCsvColumn,
  serializeCustomerContractCsvTemplateRows,
} from '~/models/customerContractCsvHeader'

/** 雛形のダミー Id。登録済みの Id には一致しないため、そのまま取り込むと「登録されていません」の行エラーになる。 */
const DUMMY_ID = '00000000-0000-0000-0000-000000000000'
const DUMMY_NUMBER = '0000000000000000000001'

/** 記入ガイド表の単一 SoT。`ColumnGuideEntry<CustomerContractCsvColumn>` で「ガイド行名 ∈ CSV 列名」を型で保つ。 */
const GUIDE_BY_COLUMN: Record<
  CustomerContractCsvColumn,
  Omit<ColumnGuideEntry<CustomerContractCsvColumn>, 'name'>
> = {
  地点Id: {
    description: '変更する地点',
    required: 'optional',
    rule: '登録済みの地点を変更する場合はダウンロードした地点Idをそのまま入力。空欄の行は新しい地点として登録します。',
    example: '(空欄)',
  },
  需要家Id: {
    description: '契約の需要家',
    required: 'optional',
    rule: '登録済みの需要家に契約を足す場合はダウンロードした需要家Idをそのまま入力。空欄の行は需要家名が同じでも新しい需要家として登録します。需要家 1 件は 1 つのエリアに属し、供給地点特定番号は 1 つです。既存の需要家に別のエリア・別の番号の地点は足せません (別の需要家として登録してください)。',
    example: '(空欄)',
  },
  区分: {
    description: '地点の区分',
    required: 'conditional',
    rule: '新しい地点の場合は「契約 / 見込み」のいずれかを入力。登録済みの地点を変更する場合、見込みを契約にするときだけ「契約」に書き換える (空欄は今の区分のまま)。',
    example: '契約',
  },
  エリア: {
    description: '供給地点のエリア',
    required: 'conditional',
    rule: '新しい地点の場合は「北海道 / 東北 / 東京 / 中部 / 北陸 / 関西 / 中国 / 四国 / 九州 / 沖縄」のいずれかを入力。登録済みの地点の行で登録済みと違うエリアを入力すると、入力ミスの訂正としてエリアを直します (失注の地点は直せません。同じ需要家の他の契約も同じエリアに直してください)。契約のエリアを直す行で、元の小売が新しいエリアの自社需要BG にいない場合は、事業者コードで新しい小売を選んでください。',
    example: '東京',
  },
  需要家名: {
    description: '需要家の名前',
    required: 'conditional',
    rule: '需要家Idが空欄の新しい地点の場合は、30文字以内で入力 (必須)。登録済みの地点の行は、登録済みの名前と違う名前を入れるとその需要家の名前の変更になり、同じ需要家のすべての契約に反映されます (空欄は変更しません)。契約が切り替わる同じ需要家の行は、1 行だけ直せば足ります (別々の新しい名前を書くとエラー)。',
    example: '(需要家名)',
  },
  供給地点特定番号: {
    description: '契約の供給地点特定番号',
    required: 'conditional',
    rule: '22桁の数字で入力 (先頭のゼロもそのまま)。先頭 2 桁はエリアの番号 (01 北海道 … 03 東京 … 10 沖縄) で、エリアの列と合わせます。契約は必須。見込みは空欄でもよく、見込みのうちは書き足し・直しができます。契約の番号は、供給地点マスタに載っておらず託送実績も無い間だけ直せます (条件外は、供給終了日で前の契約を終わらせて、正しい番号で次の契約を登録してください)。同じ需要家の他の契約も同じ番号に直してください。',
    example: '0000000000000000000001',
  },
  電圧階級: {
    description: '供給地点の電圧階級',
    required: 'conditional',
    rule: '新しい地点の場合は「特別高圧 / 高圧 / 低圧」のいずれかを入力。登録済みの地点の行で登録済みと違う電圧階級を入力すると、入力ミスの訂正として直します (受電設備が変わった場合は、供給終了日で前の契約を終わらせて次の契約を登録してください)。',
    example: '高圧',
  },
  '契約電力 (kW)': {
    description: '契約電力',
    required: 'required',
    rule: '0 より大きい数値で入力。',
    example: '500',
  },
  供給開始日: {
    description: '供給を始める日',
    required: 'required',
    rule: '「YYYY/MM/DD」の形式で入力。',
    example: '2026/09/01',
  },
  供給終了日: {
    description: '供給を終える日',
    required: 'optional',
    rule: '「YYYY/MM/DD」の形式で入力。終了日が無い場合は空欄。',
    example: '(空欄)',
  },
  事業者コード: {
    description: '契約の小売電気事業者',
    required: 'conditional',
    rule: '契約にする行で、エリアの需要BGに小売電気事業者が複数いる場合は 4桁の事業者コードで入力。1社の場合は空欄でよい。ただし契約のエリアを直す行で、元の小売が新しいエリアの自社需要BG にいない場合は、候補が1社でも入力が必須 (空欄は行エラー)。',
    example: '(空欄)',
  },
  自家発有無: {
    description: '自家発電設備があるか',
    required: 'optional',
    rule: '「1」(あり) か「0」(なし) で入力。',
    example: '0',
  },
  '自家発容量 (kW)': {
    description: '自家発電設備の容量',
    required: 'conditional',
    rule: '自家発有無が「1」の場合は 0 より大きい数値で入力。「0」の場合は空欄。',
    example: '(空欄)',
  },
  '逆調契約 (kW)': {
    description: '逆調契約の契約電力',
    required: 'optional',
    rule: '自家発容量以下の数値で入力。空欄は逆調契約なしです。',
    example: '(空欄)',
  },
  契約者Id: {
    description: '需要家の契約者',
    required: 'optional',
    rule: '新しい需要家の行で、登録済みの契約者に紐付ける場合はダウンロードした契約者Idを入力。空欄の場合は新しい契約者を作ります。登録済みの地点の行で、登録済みと違う契約者Idを入力すると、その需要家の契約者を付け替えます (空欄は変更なし)。',
    example: '(空欄)',
  },
  契約者名: {
    description: '新しい契約者の名前',
    required: 'optional',
    rule: '契約者Idが空欄の新しい需要家の行で、100文字以内で入力。空欄の場合は需要家名と同じ名前で作ります。',
    example: '(空欄)',
  },
  契約電力の変更: {
    description: '供給期間の途中で変わる契約電力',
    required: 'optional',
    rule: '「適用開始日:契約電力」をセミコロン (;) で区切って並べる (例: 2027-04-01:400;2028-04-01:350)。最初の契約電力は「契約電力 (kW)」と供給開始日の列で、ここには変更後のものだけを書きます。適用開始日は供給開始日より後で供給終了日以前の日にし、同じ日は 2 つ書けません (供給期間を縮めて外に出る期間もエラー)。この列があるファイルでは、空欄はその地点の契約電力の変更をなくします。ダウンロードのまま取り込めば変更なしです。列ごと省略したファイルは変更を一切変えません。',
    example: '(空欄)',
  },
}

/** 記入ガイド表 (列順は CSV の列順)。 */
export const CUSTOMER_CONTRACT_CSV_COLUMN_GUIDE: Array<
  ColumnGuideEntry<CustomerContractCsvColumn>
> = CUSTOMER_CONTRACT_CSV_COLUMNS.map((name) => ({
  name,
  ...GUIDE_BY_COLUMN[name],
}))

/** 記入例案内 (AC-DUC-035) の代表列と値。代表列は地点を指す「地点Id」。 */
export const CUSTOMER_CONTRACT_CSV_SAMPLE_ROW = {
  column: '地点Id',
  example: DUMMY_ID,
} as const

/**
 * 雛形の記入例 (3 行)。backend apitest が同じ値で「全行がエラーになり、何も登録されない」ことを
 * 確かめている (go/test/apitest/customer_contract_csv_test.go)。ここを変えたら Go 側も揃える。
 */
export const CUSTOMER_CONTRACT_CSV_TEMPLATE_ROWS: Array<
  Partial<Record<CustomerContractCsvColumn, string>>
> = [
  // ① 新しい需要家の新規契約 (エリアが「記入例」なので取り込まれない)
  {
    区分: '契約',
    エリア: '記入例',
    需要家名: '(需要家名)',
    供給地点特定番号: DUMMY_NUMBER,
    電圧階級: '高圧',
    '契約電力 (kW)': '500',
    供給開始日: '2026/09/01',
  },
  // ② 登録済みの需要家への新規見込み (番号は空欄でよい)
  {
    需要家Id: DUMMY_ID,
    区分: '見込み',
    エリア: '記入例',
    電圧階級: '高圧',
    '契約電力 (kW)': '300',
    供給開始日: '2026/09/01',
  },
  // ③ 登録済みの地点の変更 (地点Id がダミーなので取り込まれない)
  {
    地点Id: DUMMY_ID,
    区分: '契約',
    供給地点特定番号: DUMMY_NUMBER,
    '契約電力 (kW)': '600',
    供給開始日: '2026/09/01',
    供給終了日: '2027/03/31',
  },
]

/** 雛形ダウンロード用の CSV 本文 (ヘッダー行 + 記入例 3 行、common.md AC-DUC-031)。 */
export const buildCustomerContractCsvTemplate = (): string =>
  serializeCustomerContractCsvTemplateRows(CUSTOMER_CONTRACT_CSV_TEMPLATE_ROWS)
